<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Database Backups</span>
      <div class="d-flex ga-2">
        <v-btn
          color="warning"
          variant="outlined"
          @click="openUploadDialog"
        >
          <v-icon start>mdi-upload</v-icon>
          Restore from File
        </v-btn>
        <v-btn
          color="primary"
          @click="createBackup"
          :loading="creatingBackup"
        >
          <v-icon start>mdi-database-plus</v-icon>
          Create Backup
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-alert v-if="error" type="error" variant="tonal" closable @click:close="error = ''">
        {{ error }}
      </v-alert>

      <v-alert v-if="success" type="success" variant="tonal" closable @click:close="success = ''">
        {{ success }}
      </v-alert>

      <v-data-table
        :headers="headers"
        :items="backups"
        :loading="loading"
        :items-per-page="10"
      >
        <!-- Filename Column -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center ga-2">
            <v-icon color="primary">mdi-database</v-icon>
            <span class="font-weight-medium">{{ (item as any).name }}</span>
          </div>
        </template>

        <!-- Size Column -->
        <template v-slot:item.size="{ item }">
          {{ formatSize((item as any).size) }}
        </template>

        <!-- Created At Column -->
        <template v-slot:item.date="{ item }">
          {{ formatDateTime((item as any).date) }}
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex ga-1">
            <v-btn
              icon="mdi-download"
              size="small"
              variant="text"
              color="primary"
              @click="downloadBackup(item as any)"
              title="Download Backup"
            ></v-btn>
            <v-btn
              icon="mdi-restore"
              size="small"
              variant="text"
              color="warning"
              @click="openRestoreDialog(item as any)"
              title="Restore from Backup"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="openDeleteDialog(item as any)"
              title="Delete Backup"
            ></v-btn>
          </div>
        </template>

        <!-- Loading -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
        </template>

        <!-- No Data -->
        <template v-slot:no-data>
          <v-alert type="info" variant="tonal" class="ma-4">
            No backups available. Create your first backup to get started.
          </v-alert>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Restore Backup Dialog -->
    <v-dialog v-model="showRestoreDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="warning" start>mdi-alert</v-icon>
          Restore Backup
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>Warning:</strong> Restoring from a backup will replace all current data with the backup data. This action cannot be undone.
          </v-alert>
          <p class="text-body-1">
            Are you sure you want to restore from <strong>{{ backupToRestore?.name }}</strong>?
          </p>
          <p class="text-caption text-grey">
            Created: {{ formatDateTime(backupToRestore?.date) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeRestoreDialog">Cancel</v-btn>
          <v-btn
            color="warning"
            @click="confirmRestore"
            :loading="restoring"
          >
            Restore
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Backup Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          Delete Backup
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">
            Are you sure you want to delete <strong>{{ backupToDelete?.name }}</strong>?
          </p>
          <p class="text-caption text-grey">
            This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
            :loading="deleting"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload Restore Dialog -->
    <v-dialog v-model="showUploadDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          Restore from Uploaded Backup
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>Warning:</strong> This will replace all current data. Make sure you have a current backup before proceeding.
          </v-alert>

          <v-file-input
            v-model="uploadFile"
            label="Select backup file"
            accept=".json,.zip,.gz"
            prepend-icon="mdi-database"
            show-size
            :error-messages="uploadError"
          ></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeUploadDialog">Cancel</v-btn>
          <v-btn
            color="warning"
            @click="uploadAndRestore"
            :loading="uploading"
            :disabled="!uploadFile"
          >
            Upload & Restore
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed, onMounted } from 'vue'
import { useBackupStore } from '@/store/backup'

const backupStore = useBackupStore()

const creatingBackup = ref(false)
const restoring = ref(false)
const deleting = ref(false)
const uploading = ref(false)
const error = ref('')
const success = ref('')

const showRestoreDialog = ref(false)
const showDeleteDialog = ref(false)
const showUploadDialog = ref(false)
const backupToRestore = ref<any>(null)
const backupToDelete = ref<any>(null)
const uploadFile = ref<File | File[] | null>(null)
const uploadError = ref('')

const headers = [
  { title: 'Filename', key: 'name', sortable: true },
  { title: 'Size', key: 'size', sortable: true },
  { title: 'Created', key: 'date', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const backups = computed(() => backupStore.getBackups)
const loading = computed(() => backupStore.isLoading)

const loadBackups = async () => {
  await backupStore.fetchBackups()
}

const createBackup = async () => {
  creatingBackup.value = true
  error.value = ''
  success.value = ''

  const result = await backupStore.createBackup()

  if (result.success) {
    success.value = result.message
  } else {
    error.value = result.message
  }

  creatingBackup.value = false
}

const downloadBackup = async (backup: any) => {
  error.value = ''
  success.value = ''

  const result = await backupStore.downloadBackup(backup)

  if (result.success) {
    success.value = result.message
  } else {
    error.value = result.message
  }
}

const openRestoreDialog = (backup: any) => {
  backupToRestore.value = backup
  showRestoreDialog.value = true
}

const closeRestoreDialog = () => {
  showRestoreDialog.value = false
  backupToRestore.value = null
}

const confirmRestore = async () => {
  if (!backupToRestore.value) return

  restoring.value = true
  error.value = ''
  success.value = ''

  const result = await backupStore.restoreBackup(backupToRestore.value.name)

  if (result.success) {
    success.value = result.message
    closeRestoreDialog()
  } else {
    error.value = result.message
  }

  restoring.value = false
}

const openDeleteDialog = (backup: any) => {
  backupToDelete.value = backup
  showDeleteDialog.value = true
}

const closeDeleteDialog = () => {
  showDeleteDialog.value = false
  backupToDelete.value = null
}

const confirmDelete = async () => {
  if (!backupToDelete.value) return

  deleting.value = true
  error.value = ''
  success.value = ''

  const result = await backupStore.deleteBackup(backupToDelete.value.name)

  if (result.success) {
    success.value = result.message
    closeDeleteDialog()
  } else {
    error.value = result.message
  }

  deleting.value = false
}

const openUploadDialog = () => {
  uploadFile.value = null
  uploadError.value = ''
  showUploadDialog.value = true
}

const closeUploadDialog = () => {
  showUploadDialog.value = false
  uploadFile.value = null
  uploadError.value = ''
}

const uploadAndRestore = async () => {
  if (!uploadFile.value) {
    uploadError.value = 'Please select a file'
    return
  }

  // Handle both single File and File[] cases
  let file: File
  if (Array.isArray(uploadFile.value)) {
    if (uploadFile.value.length === 0) {
      uploadError.value = 'Please select a file'
      return
    }
    file = uploadFile.value[0]
  } else {
    file = uploadFile.value
  }

  uploading.value = true
  uploadError.value = ''
  error.value = ''
  success.value = ''

  const result = await backupStore.uploadAndRestore(file)

  if (result.success) {
    success.value = result.message
    closeUploadDialog()
  } else {
    uploadError.value = result.message
  }

  uploading.value = false
}

const formatSize = (bytes: number) => {
  if (!bytes) return 'N/A'
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

const formatDateTime = (date: string) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleString()
}

onMounted(() => {
  loadBackups()
})

// Expose openUploadDialog for parent component
defineExpose({ openUploadDialog })
</script>

<style scoped>
.font-weight-medium {
  font-weight: 500;
}
</style>
